import React, { useEffect, useState } from "react";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { useLang } from "../LanguageContext";
import { LANG_CODES, LANG_LABELS } from "../i18n";
import { RESTAURANT, telLink, waLink } from "../restaurant.config";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#home", label: t.nav.home, id: "nav-link-home" },
    { href: "#storia", label: t.nav.storia, id: "nav-link-storia" },
    { href: "#menu", label: t.nav.menu, id: "nav-link-menu" },
    { href: "#galleria", label: t.nav.galleria, id: "nav-link-galleria" },
    { href: "#contatti", label: t.nav.contatti, id: "nav-link-contatti" },
  ];

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F5F2EC]/85 border-b border-[#23211E]/10"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        {/* Brand */}
        <button
          data-testid="nav-brand"
          onClick={() => go("#home")}
          className="flex items-baseline gap-2 group"
        >
          <span
            className={`font-serif text-xl md:text-2xl leading-none tracking-tight transition-colors ${
              scrolled ? "text-[#23211E]" : "text-[#F5F2EC]"
            }`}
          >
            Grappolo d'Oro
          </span>
          <span
            className={`hidden md:inline text-[10px] uppercase tracking-[0.25em] transition-colors ${
              scrolled ? "text-[#565F41]" : "text-[#F5F2EC]/70"
            }`}
          >
            Monforte d'Alba · {RESTAURANT.since}
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={l.id}
              onClick={() => go(l.href)}
              className={`text-xs uppercase tracking-[0.22em] transition-colors hover:text-[#B85532] ${
                scrolled ? "text-[#23211E]" : "text-[#F5F2EC]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Right side: lang + call */}
        <div className="flex items-center gap-3 md:gap-5">
          <div
            data-testid="lang-toggle-btn"
            className={`flex items-center text-[11px] tracking-[0.25em] transition-colors ${
              scrolled ? "text-[#23211E]" : "text-[#F5F2EC]"
            }`}
          >
            {LANG_CODES.map((code, i) => (
              <React.Fragment key={code}>
                <button
                  data-testid={`lang-toggle-${code}`}
                  onClick={() => setLang(code)}
                  className={`px-1.5 py-1 transition-all ${
                    lang === code
                      ? "text-[#B85532] font-medium"
                      : "opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`Switch language to ${LANG_LABELS[code]}`}
                >
                  {LANG_LABELS[code]}
                </button>
                {i < LANG_CODES.length - 1 && (
                  <span className="opacity-40">/</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <a
            data-testid="nav-call-btn"
            href={telLink("mobile")}
            className={`hidden md:inline-flex items-center gap-2 px-4 py-2 border text-[11px] uppercase tracking-[0.22em] transition-all hover:bg-[#B85532] hover:text-white hover:border-[#B85532] ${
              scrolled
                ? "border-[#23211E] text-[#23211E]"
                : "border-[#F5F2EC] text-[#F5F2EC]"
            }`}
          >
            <Phone className="w-3 h-3" strokeWidth={1.5} />
            {RESTAURANT.mobileDisplay}
          </a>

          <a
            data-testid="nav-reserve-btn"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center px-5 py-2 bg-[#B85532] text-white text-[11px] uppercase tracking-[0.22em] hover:-translate-y-0.5 hover:bg-[#a04825] transition-all"
          >
            {t.nav.prenota}
          </a>

          {/* mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${
              scrolled ? "text-[#23211E]" : "text-[#F5F2EC]"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? (
              <X className="w-6 h-6" strokeWidth={1.25} />
            ) : (
              <MenuIcon className="w-6 h-6" strokeWidth={1.25} />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden bg-[#F5F2EC] border-t border-[#23211E]/10 px-6 py-8 flex flex-col gap-5"
        >
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-${l.id}`}
              onClick={() => go(l.href)}
              className="text-left font-serif text-2xl text-[#23211E] hover:text-[#B85532] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="mobile-reserve-btn"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center justify-center px-5 py-3 bg-[#B85532] text-white text-xs uppercase tracking-[0.22em]"
          >
            {t.nav.prenota}
          </a>
        </div>
      )}
    </header>
  );
}
